import * as mobx from 'mobx'
import * as lodash from 'lodash'

/**
 * compares deeply, sets shallow
 * @param {*} $scope - an angular scope
 * @param {*} scopeVariable - the angular scope variable to set into
 * @param {IObservableObject} mobxObservable
 * @param {boolean} debug - log updates
 * @param {Function} angularToMobx - function to map angular object to another object of fields to set on mobx object
 * @param {Function} mobxToAngular - function to map mobx object to another object of fields to set on angular scope object
 * @param {boolean} initializeFromMobx - sets angular initially from mobx if true, otherwise vice versa
 * @param {number} debounceToAngular - milliseconds to debounce updates to angular (e.g. to prevent excessive watches from firing and duplicating requests)
 * @return {Function} - function to stop binding (also shuts down when $scope is destroyed)
 */
export function bindShallow(
  $scope,
  scopeVariable,
  mobxObservable,
  {
    debug = false,
    initializeFromMobx = false,
    angularToMobx = angular => angular,
    mobxToAngular = mobx => mobx,
    debounceToAngular = 0
  } = {}
) {

  function run() {

    let pendingAngular = {};
    const applyAngularDebounced = lodash.debounce(() => {
      Object.assign(scopeVariable, pendingAngular);
      pendingAngular = {};
      $scope.$apply()
    }, debounceToAngular);

    function pushToAngular(updated) {
      if (debug) {
        console.info('mobx observable updated, update angular', {mobx: updated, angular: {...scopeVariable}})
        Object.keys(updated).forEach(x => {
          if (!lodash.isEqual(updated[x], scopeVariable[x])) {
            console.info(' ->', { field: x, mobx: updated[x], angular: scopeVariable[x] })
          }
        })
      }
      Object.assign(pendingAngular, updated)
      applyAngularDebounced()
    }

    function pushToMobx(newVal) {
      if (debug) {
        const jsObservable = mobx.toJS(mobxObservable)
        console.info('angular scope updated, update mobx', {angular: newVal, mobx: jsObservable})
        Object.keys(newVal).forEach(x => {
          if (!lodash.isEqual(newVal[x], jsObservable[x])) {
            console.info(' ->', { field: x, angular: scopeVariable[x], mobx: jsObservable[x] })
          }
        })
      }
      mobx.runInAction(() => Object.assign(mobxObservable, newVal))
    }
    // mobx -> angular
    const reactionDisposer = mobx.reaction(
      () => mobxToAngular(mobx.toJS(mobxObservable)),
      pushToAngular,
      { equals: mobx.comparer.structural }
    );

    if (initializeFromMobx) {
      pushToAngular(mobxToAngular(mobx.toJS(mobxObservable)))
    } else {
      // set mobx initially from angular
      pushToMobx(angularToMobx({...scopeVariable}))
    }

    // angular -> mobx
    $scope.$watch(
      () => angularToMobx({...scopeVariable}),
      pushToMobx,
      true
    );

    function cleanup() {
      reactionDisposer()
    }

    $scope.$on('$destroy', cleanup)
    return cleanup
  }
  return run()

}
